import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Crown, CheckSquare, LayoutGrid, List, SlidersHorizontal, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import MediaImage from "@/components/MediaImage";
import { WATCHLIST_PAGE_SIZE } from "@/media";
import { useMeta } from "@/contexts/MetaContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSeo } from "@/seo";
import EmptyState from "@/components/EmptyState";
import PlayerCard, { playerLink } from "@/components/PlayerCard";
import MatchesStrip from "@/components/MatchesStrip";
import CollectionStrip from "@/components/CollectionStrip";
import CardHits from "@/components/CardHits";
import { DEFAULT_FILTERS, PREMIUM_KEYS, WatchlistFilters, activeFilterLabels } from "@/components/WatchlistFilters";

function filtersFromParams(sp) {
  const f = { ...DEFAULT_FILTERS };
  Object.keys(DEFAULT_FILTERS).forEach((k) => {
    if (!sp.has(k)) return;
    const v = sp.get(k);
    f[k] = typeof DEFAULT_FILTERS[k] === "boolean" ? v === "1" : v;
  });
  return f;
}
function paramsFromFilters(f) {
  const out = {};
  Object.keys(DEFAULT_FILTERS).forEach((k) => {
    if (f[k] === DEFAULT_FILTERS[k] || f[k] === "" || f[k] === false) return;
    out[k] = f[k] === true ? "1" : String(f[k]);
  });
  return out;
}

const BANNER_IMG = "https://static.prod-images.emergentagent.com/jobs/199a16d4-02ea-4525-bb76-5eb1d833d710/images/09ee6675485812de14351733319be290e743b3e9c09c17f0967e5789d40297a9.jpeg";
const WHY_KEYS = new Set(["called_up_not_played", "played_not_started", "senior_history_unseen", "milestone_started", "milestone_scored", "minutes_trend", "age_bonus", "media_buzz", "rumored_play", "club_spotlight", "card_exists"]);
const MILESTONE_SEGS = ["called_up", "debuted", "started", "scored"]; // segment 5 reserved — gold only when reached
const MIL_CODE = { called_up: "CLD", debuted: "DEB", started: "STA", scored: "SCR" };

const DASH = "—";
const val = (v) => (v === null || v === undefined || v === "" ? DASH : v);

const LEAGUE_CODES = { "Premier League": "EPL", "La Liga": "LAL", "Serie A": "SEA", "Bundesliga": "BUN", "Ligue 1": "LI1", "Primeira Liga": "PRI", "Eredivisie": "ERE", "Jupiler Pro League": "JPL" };

function leagueCode(name) {
  if (!name) return DASH;
  if (LEAGUE_CODES[name]) return LEAGUE_CODES[name]; // explicit: initials collide (Premier League / Primeira Liga → "PL")
  const words = name.split(/\s+/).filter(Boolean);
  const letters = words.map((w) => w[0]).join("").toUpperCase();
  const digits = (name.match(/\d+/g) || []).join("");
  return (letters + digits).slice(0, 4) || DASH;
}

export default function Watchlist() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const meta = useMeta();
  const { me } = useAuth();
  useSeo(t("seo.home_title"), t("seo.home_desc"), "/");
  const [data, setData] = useState(null);
  const [matches, setMatches] = useState({ upcoming: [], recent: [] });
  const [myList, setMyList] = useState([]);
  const [listLimit, setListLimit] = useState({ used: 0, limit: null });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("cards");
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const setFilters = useCallback((next) => setSearchParams(paramsFromFilters(next), { replace: true }), [setSearchParams]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const premium = !!me && (me.premium_access);

  const buildParams = useCallback(() => {
    const params = { limit: WATCHLIST_PAGE_SIZE };
    if (filters.league) params.league = filters.league;
    if (filters.state) params.state = filters.state;
    if (filters.club) params.club = filters.club;
    if (filters.search) params.search = filters.search;
    if (filters.status !== "watching") params.status = filters.status;
    if (filters.card === "with") params.has_card = true;
    if (filters.card === "without") params.has_card = false;
    if (filters.rc_card) params.has_rc_card = true;
    if (me && filters.my_list) params.my_list = true;
    if (premium) {
      PREMIUM_KEYS.forEach((k) => { if (filters[k] !== "") params[k] = filters[k]; });
      if (filters.sort !== "hype") params.sort = filters.sort;
    }
    return params;
  }, [filters, premium, me]);

  useEffect(() => {
    // /api/meta est demandé une seule fois par chargement, partagé via MetaContext :
    // plus de requêtes concurrentes annulables, et une erreur ne conclut jamais à la
    // place du serveur (meta reste null = « on ne sait pas encore »).
    Promise.all([
      api.get("/matches", { params: { kind: "upcoming" } }),
      api.get("/matches", { params: { kind: "recent" } }),
    ]).then(([u, r]) => setMatches({ upcoming: u.data.matches, recent: r.data.matches })).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (me) {
      api.get("/lists/ids").then((r) => {
        setMyList(r.data.ids || []);
        setListLimit({ used: r.data.used ?? 0, limit: r.data.limit ?? null });
      }).catch(() => {});
    }
  }, [me, api]);

  // Movers feed : dérivé du flux non filtré DÉJÀ chargé par la page (l'appel
  // /api/watchlist?limit=50 doublonnait la requête principale). Quand des filtres sont
  // actifs, le flux affiché n'est plus représentatif : une lecture non filtrée dédiée est
  // alors faite, une seule fois. Masqué entièrement si aucun joueur ne porte de delta.
  const unfiltered = useMemo(() => Object.keys(buildParams()).length === 0, [buildParams]);
  const [moversRows, setMoversRows] = useState(null);
  useEffect(() => {
    if (!me || unfiltered) return;
    api.get("/watchlist", { params: { limit: 50 } })
      .then((r) => setMoversRows(r.data.players || []))
      .catch(() => {});
  }, [me, unfiltered, api]);
  const movers = useMemo(() => {
    if (!me) return [];
    const src = unfiltered ? (data?.players || []) : (moversRows || []);
    return src
      .filter((p) => p.signal_delta_7d !== null && p.signal_delta_7d !== undefined)
      .sort((a, b) => b.signal_delta_7d - a.signal_delta_7d)
      .slice(0, 6);
  }, [me, unfiltered, data, moversRows]);

  const [loadFailed, setLoadFailed] = useState(false);
  const load = useCallback((tries = 2) => {
    setLoading(true);
    api.get("/watchlist", { params: buildParams() })
      .then((r) => { setData(r.data); setLoadFailed(false); })
      // Un appel interrompu n'est pas une liste vide. Le catch écrivait
      // `{players: [], total: 0}` : la page annonçait alors « aucun rookie » ou
      // « données en cours de reconstruction » alors que le serveur répondait 929 joueurs
      // (constaté en production le 22/09/2026, avec des net::ERR_ABORTED sur /api/watchlist).
      // On réessaie, on garde ce qu'on avait, et on ne fabrique jamais un total.
      .catch(() => {
        if (tries > 0) { setTimeout(() => load(tries - 1), 1500); return; }
        setLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }, [buildParams, api]);

  const [loadingMore, setLoadingMore] = useState(false);
  const loadMore = () => {
    if (!data || loadingMore) return;
    setLoadingMore(true);
    api.get("/watchlist", { params: { ...buildParams(), skip: data.players.length, limit: WATCHLIST_PAGE_SIZE } })
      .then((r) => setData((d) => ({ ...r.data, players: [...(d?.players || []), ...(r.data.players || [])] })))
      .finally(() => setLoadingMore(false));
  };

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  const toggleList = async (p) => {
    if (!me) {
      navigate("/auth");
      return;
    }
    const key = `${p.provider}:${p.player_id}`;
    const was = myList.includes(key);
    setMyList((cur) => (was ? cur.filter((k) => k !== key) : [...cur, key])); // immediate feedback
    try {
      const r = await api.post("/lists/toggle", { player_id: p.player_id, provider: p.provider });
      setMyList((cur) => (r.data.in_list ? (cur.includes(key) ? cur : [...cur, key]) : cur.filter((k) => k !== key)));
      setListLimit({ used: r.data.used, limit: r.data.limit ?? null });
      toast.success(r.data.in_list ? t("player.list_added") : t("player.list_removed"));
    } catch (e) {
      setMyList((cur) => (was ? (cur.includes(key) ? cur : [...cur, key]) : cur.filter((k) => k !== key)));
      if (e?.response?.data?.detail === "free_limit_reached") {
        toast(t("filters.free_limit_title", { n: meta?.free_list_limit ?? 1 }), {
          description: t("filters.free_limit_upsell"),
          action: { label: t("hero.cta"), onClick: () => navigate("/pricing") },
          duration: 8000,
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
  };
  const atFreeLimit = listLimit.limit !== null && listLimit.used >= listLimit.limit;

  const whyOf = (p) => {
    const r = (p.top_reasons || []).find((x) => WHY_KEYS.has(x.key));
    if (r) return t(`reasons_short.${r.key}`, r.params || {});
    if (p.milestone_state && p.milestone_state !== "none") return t(`state.${p.milestone_state}`);
    return DASH;
  };

  // Seule une réponse EXPLICITE du serveur autorise ce verdict : `meta` absent signifie
  // « pas encore répondu », jamais « pas de source ».
  if (meta && meta.provider_configured === false) {
    return (
      <div className="pt-10">
        <EmptyState testid="empty-state-data-source" title={t("empty.no_source_title")} body={t("empty.no_source_body")} />
      </div>
    );
  }

  const activeLabels = activeFilterLabels(filters, t);
  const filtered = activeLabels.length > 0;

  return (
    <div className="wl-scope -mx-4 -mb-16 min-h-[80vh] px-4 pt-4 pb-12 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      {/* BANNER */}
      <section data-testid="hero-section" className="relative overflow-hidden border border-[var(--wl-line)]">
        <img src={BANNER_IMG} alt="" className="absolute inset-0 h-full w-full object-cover object-right" />
        <div className="absolute inset-0 bg-[#0B0E14]/55" />
        <div className="relative px-5 py-5 sm:px-8 sm:py-7">
          <h1 className="max-w-xl whitespace-pre-line font-sg text-xl font-bold uppercase leading-[1.05] tracking-[-0.035em] text-[var(--wl-text)] sm:text-3xl lg:text-4xl" data-testid="banner-title">
            {t("wl.banner_title")}
          </h1>
          <p className="mt-2.5 max-w-md wl-mono text-[10.5px] leading-relaxed text-[var(--wl-muted)]" data-testid="banner-baseline">
            {t("wl.banner_sub")}
          </p>
          {/* Deuxième promesse : la home ne parlait que du suivi des joueurs, le compteur de
              collection était invisible pour un nouveau visiteur. */}
          <p className="mt-1.5 max-w-md font-sg text-[13px] font-bold uppercase tracking-[-0.02em] text-[var(--wl-text)] sm:text-[15px]" data-testid="banner-promise-collection">
            {t("wl.banner_promise2")}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {!premium && (
              <Link to="/pricing" data-testid="hero-premium-cta" className="inline-flex items-center gap-1.5 border border-[var(--wl-accent)] bg-[var(--wl-accent)] px-3.5 py-1.5 wl-mono text-[10px] font-bold uppercase tracking-[.04em] text-[#0B0E14] transition-colors hover:bg-transparent hover:text-[var(--wl-accent)]">
                <Crown className="h-3.5 w-3.5" /> {t("hero.cta")}
              </Link>
            )}
            <Link to="/my-collection" data-testid="hero-collection-cta" className="inline-flex items-center gap-1.5 border border-[var(--wl-line)] bg-[#0B0E14]/60 px-3.5 py-1.5 wl-mono text-[10px] font-bold uppercase tracking-[.04em] text-[var(--wl-text)] transition-colors hover:border-[var(--wl-accent)] hover:text-[var(--wl-accent)]">
              <CheckSquare className="h-3.5 w-3.5" /> {t("wl.banner_collection_cta")}
            </Link>
          </div>
        </div>
      </section>

      {/* 1 — HYPE MOVERS */}
      {movers.length > 0 && (
        <section className="mt-4" data-testid="hype-movers-strip">
          <div className="mb-2 flex items-baseline gap-3">
            <h2 className="font-sg text-lg font-bold uppercase tracking-[-0.035em] text-[var(--wl-text)]">{t("wl.movers")}</h2>
            <Link to="/hype" data-testid="hype-explainer-link-movers" className="wl-mono text-[9px] uppercase tracking-[.04em] text-[var(--wl-accent)] hover:underline">
              {t("hype.link")}
            </Link>
          </div>
          <div className="wl-strip">
            {movers.map((p) => (
              <Link key={`mv-${p.provider}:${p.player_id}`} to={playerLink(p)} className="wl-mover" data-testid={`mover-card-${p.player_id}`}>
                <p className={`wl-mono text-[10.5px] ${p.signal_delta_7d > 0 ? "text-[var(--wl-green)]" : "text-[var(--wl-muted)]"}`} data-testid={`mover-delta-${p.player_id}`}>
                  {p.signal_delta_7d > 0 ? "+" : ""}{p.signal_delta_7d} HYPE · {lang === "fr" ? "7J" : "7D"}
                </p>
                <p className="mt-1 truncate font-sg text-[12.5px] font-bold uppercase tracking-[-0.01em] text-[var(--wl-text)]">{p.name}</p>
                <p className="wl-mono text-[9px] text-[var(--wl-muted)] truncate">{val(p.club_name)}</p>
                <p className="mt-1.5 border-t border-[var(--wl-line)] pt-1.5 wl-mono text-[9px] leading-snug text-[var(--wl-muted)]">{whyOf(p)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* 2 — COMPETITION CHIPS */}
      <section className="mt-4" data-testid="watchlist-league-chips">
        <div className="wl-strip">
          <button
            className="wl-chip" data-active={!filters.league} data-testid="chip-league-all"
            onClick={() => setFilters({ ...filters, league: "", club: "" })}
          >
            {t("wl.all")}<b>{val(meta?.players_tracked)}</b>
          </button>
          {(meta?.leagues || []).map((l) => (
            <button
              key={l.id} className="wl-chip" data-active={String(filters.league) === String(l.id)}
              data-testid={`chip-league-${l.id}`}
              onClick={() => setFilters({ ...filters, league: String(filters.league) === String(l.id) ? "" : l.id, club: "" })}
            >
              {l.name}<b>{l.players}</b>
            </button>
          ))}
        </div>
      </section>

      {/* FILTER ROW — free filters open, premium ones shown locked; collapsed behind a toggle on mobile */}
      <div className="mt-3 lg:hidden">
        <button type="button" onClick={() => setFiltersOpen((o) => !o)} data-testid="watchlist-filters-toggle"
          className="flex w-full items-center gap-2 border border-[var(--wl-line)] px-3 py-2 wl-mono text-[10px] uppercase tracking-[.04em] text-[var(--wl-text)]">
          <SlidersHorizontal className="h-3.5 w-3.5" /> {t("filters.title")}
          {activeLabels.length > 0 && <b className="ml-1 text-[var(--wl-accent)]" data-testid="watchlist-filters-active-count">{activeLabels.length}</b>}
          <span className="ml-auto text-[var(--wl-muted)]">{filtersOpen ? "▴" : "▾"}</span>
        </button>
      </div>
      <div className={`mt-3 ${filtersOpen ? "block" : "hidden"} lg:block`} data-testid="watchlist-filters-wrapper">
        <WatchlistFilters filters={filters} setFilters={setFilters} premium={premium} me={me} clubs={data?.clubs} rcCount={data?.rc_card_count}
          listLimit={listLimit} atFreeLimit={atFreeLimit} onMyList={() => (me ? setFilters({ ...filters, my_list: !filters.my_list }) : navigate("/auth"))} t={t} lang={lang} />
      </div>

      {/* 3 — PLAYER GRID */}
      <section className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="wl-mono text-[9px] uppercase tracking-[.04em] text-[var(--wl-muted)]" data-testid="watchlist-total">
            {data ? `${data.total} · ${t("nav.watchlist")}` : ""}
          </span>
          <div className="flex gap-1" data-testid="watchlist-view-toggle">
            <button
              data-testid="view-cards-btn"
              onClick={() => setView("cards")}
              aria-label={t("wl.view_cards")}
              className={`border p-1.5 transition-colors ${view === "cards" ? "border-[var(--wl-accent)] text-[var(--wl-accent)]" : "border-[var(--wl-line)] text-[var(--wl-muted)] hover:text-[var(--wl-text)]"}`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid="view-list-btn"
              onClick={() => setView("list")}
              aria-label={t("wl.view_list")}
              className={`border p-1.5 transition-colors ${view === "list" ? "border-[var(--wl-accent)] text-[var(--wl-accent)]" : "border-[var(--wl-line)] text-[var(--wl-muted)] hover:text-[var(--wl-text)]"}`}
            >
              <List className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {loading && <p className="py-10 wl-mono text-[10px] uppercase tracking-[.04em] text-[var(--wl-muted)]">{t("common.loading")}</p>}

        {!loading && loadFailed && !data && (
          <div className="border-l-2 border-[#FFD600] pl-3" data-testid="watchlist-connection-lost">
            <p className="wl-mono text-[10.5px] uppercase tracking-[.04em] text-[var(--wl-muted)]">{t("wl.connection_lost")}</p>
            <button type="button" onClick={() => load()} className="mt-1 wl-mono text-[10px] uppercase tracking-[.04em] text-[var(--wl-accent)] hover:underline" data-testid="watchlist-retry-button">
              {t("wl.retry")}
            </button>
          </div>
        )}

        {/* A search that matches only cards must show the cards, not an absence followed
            by a presence: the empty state is suppressed when card hits exist. */}
        {!loading && data && data.total === 0 && !(data.card_hits || []).length && (
          <div className="border-l-2 border-[var(--wl-accent)] pl-3" data-testid={filtered ? "empty-state-no-results" : "empty-state-rebuilding"}>
            <p className="wl-mono text-[10.5px] uppercase tracking-[.04em] text-[var(--wl-muted)]">
              {filtered ? `${t("wl.empty_filtered")} — ${activeLabels.join(" · ")}` : t("wl.empty_rebuild")}
            </p>
            {filtered && filters.search && (
              <a href={`/cards?q=${encodeURIComponent(filters.search)}`} className="mt-1 inline-block wl-mono text-[10px] text-[var(--wl-accent)] hover:underline" data-testid="search-cards-hint">
                {t("cards.search_cards_for")} « {filters.search} »
              </a>
            )}
          </div>
        )}

        {!loading && view === "cards" && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-[9px]" data-testid="watchlist-grid">
            {(data?.players || []).map((p) => {
              const delta = p.signal_delta_7d;
              const badge = p.milestone_state === "called_up" ? "imminent" : (delta > 0 ? "delta" : null);
              const segOn = MILESTONE_SEGS.indexOf(p.milestone_state) + 1; // 0 when none/unknown
              return (
                <Link key={`${p.provider}:${p.player_id}`} to={playerLink(p)} className="wl-gcard" data-testid={`player-card-${p.player_id}`}>
                  <button
                    data-testid={`grid-list-star-${p.player_id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleList(p); }}
                    aria-label={myList.includes(`${p.provider}:${p.player_id}`) ? t("player.remove_hint") : t("player.add_to_list")}
                    title={myList.includes(`${p.provider}:${p.player_id}`) ? t("player.remove_hint") : t("player.add_to_list")}
                    className="absolute bottom-1.5 right-1.5 z-10 p-1 text-[var(--wl-muted)] transition-colors hover:text-[#FFD600]"
                  >
                    <Star className={`h-3.5 w-3.5 ${myList.includes(`${p.provider}:${p.player_id}`) ? "fill-[#FFD600] text-[#FFD600]" : ""}`} />
                  </button>
                  {badge === "imminent" && <span className="wl-badge" data-kind="imminent" data-testid={`badge-imminent-${p.player_id}`}>{t("wl.imminent")}</span>}
                  {badge === "delta" && <span className="wl-badge" data-kind="delta" data-testid={`badge-delta-${p.player_id}`}>+{delta}</span>}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="wl-score" data-testid={`player-score-${p.player_id}`}>{val(p.signal_score)}</div>
                      <div className="wl-mono text-[8px] uppercase text-[var(--wl-muted)]">Hype</div>
                      <div className="mt-1.5 border-t border-[var(--wl-line)] pt-1 wl-mono text-[9px] uppercase text-[var(--wl-muted)]">{val(p.position)}</div>
                    </div>
                    <span className="wl-cartouche" data-testid={`player-club-crest-${p.player_id}`}>
                      <MediaImage src={p.club_logo} alt={t("media.logo_alt", { club: p.club_name || "—" })} kind="club" size={36} radius="rounded-none" surface="watchlist" imgClassName="object-contain p-0.5" testid={`player-club-crest-media-${p.player_id}`} />
                    </span>
                  </div>
                  <div className="mt-2 border-t border-[var(--wl-line)] pt-1.5">
                    <p className="truncate font-sg text-[12.5px] font-bold uppercase tracking-[-0.01em] text-[var(--wl-text)]">{p.name}</p>
                    {p.name_complete === false && (
                      <p className="truncate wl-mono text-[8px] uppercase tracking-wider text-[#FFD600]/80" data-testid={`player-name-incomplete-${p.player_id}`}>
                        {t("player.name_incomplete")}
                      </p>
                    )}
                    <p className="truncate wl-mono text-[9px] text-[var(--wl-muted)]">{val(p.club_name)}</p>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 wl-mono text-[9px]" data-testid={`player-stats-${p.player_id}`}>
                    <span className="text-[var(--wl-muted)]">AGE <span className="text-[var(--wl-text)]">{val(p.age)}</span></span>
                    <span className="text-[var(--wl-muted)]">LGE <span className="text-[var(--wl-text)]">{leagueCode(p.league_name)}</span></span>
                    <span className="text-[var(--wl-muted)]">MIN <span className="text-[var(--wl-text)]">{val(p.minutes_avg)}</span></span>
                    <span className="text-[var(--wl-muted)]">MIL <span className="text-[var(--wl-text)]">{MIL_CODE[p.milestone_state] || DASH}</span></span>
                    <span className="text-[var(--wl-muted)]">PRS <span className="text-[var(--wl-text)]">{val(p.press_count)}</span></span>
                  </div>
                  <div className="mt-auto flex gap-[3px] pt-2" data-testid={`player-milestones-${p.player_id}`}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <span key={i} className="wl-seg" data-on={i < segOn ? "true" : "false"} />
                    ))}
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {!loading && view === "list" && (
          <div className="flex flex-col gap-2" data-testid="watchlist-list">
            {(data?.players || []).map((p) => (
              <PlayerCard
                key={`${p.provider}:${p.player_id}`}
                p={p}
                inList={myList.includes(`${p.provider}:${p.player_id}`)}
                onToggleList={toggleList}
              />
            ))}
          </div>
        )}

        {!loading && <CardHits hits={data?.card_hits} t={t} />}

        {!loading && data && data.players.length < data.total && (
          <div className="mt-6 flex items-center gap-3">
            <button type="button" onClick={loadMore} disabled={loadingMore} className="wl-mono rounded-md border border-[var(--wl-line)] px-4 py-2 text-[10px] uppercase tracking-[.06em] text-[var(--wl-text)] transition-colors hover:border-[var(--wl-accent)] disabled:opacity-50" data-testid="watchlist-load-more">
              {loadingMore ? t("common.loading") : `${t("wl.load_more")} · ${data.players.length}/${data.total}`}
            </button>
          </div>
        )}

        <p className="mt-3 border-t border-[var(--wl-line)] pt-2 wl-mono text-[8.5px] uppercase leading-relaxed tracking-[.04em] text-[var(--wl-muted)]" data-testid="watchlist-legend">
          {t("wl.legend")}
        </p>
      </section>

      <CollectionStrip t={t} />
      <div className="mt-8" data-testid="watchlist-matches-wrapper">
        <MatchesStrip matches={matches} t={t} lang={lang} />
      </div>
    </div>
  );
}
